import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import {
  FiPackage,
  FiDollarSign,
  FiGrid,
  FiActivity,
  FiSave,
  FiArrowRight,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiMapPin
} from 'react-icons/fi';
import {
  FaBox,
  FaMoneyBillWave,
  FaRulerCombined,
  FaToggleOn,
  FaGem
} from 'react-icons/fa';

const schema = yup.object().shape({
  title: yup.string().required('Title is required').min(3, 'Title must be at least 3 characters'),
  price: yup
    .number()
    .typeError('Price must be a number')
    .positive('Price must be positive')
    .required('Price is required'),
  width: yup
    .number()
    .typeError('Width must be a number')
    .positive('Width must be positive')
    .required('Width is required'),
  length: yup
    .number()
    .typeError('Length must be a number')
    .positive('Length must be positive')
    .required('Length is required'),
  status: yup.string().oneOf(['active', 'inactive']).required()
});

const AddNewPackage = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      price: '',
      width: '',
      length: '',
      status: 'active',
      row: 1,
      col: 1,
      gridWidth: 1,
      gridHeight: 1,
      plotNumber: '',
      plotStatus: 'available',
      facing: 'North',
      location: '',
      description: '',
      features: [],
      nearby: [],
      photos: [],
      isCorner: false,
      isPremium: false,
      sizeCategory: 'medium'
    }
  });

  const watchedWidth = watch('width');
  const watchedLength = watch('length');
  const calculatedArea = watchedWidth && watchedLength ? watchedWidth * watchedLength : null;

  const onSubmit = async (data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to create package');
      }

      const result = await response.json();
      Swal.fire({
        title: "Success!",
        text: "Package created successfully.",
        icon: "success",
        confirmButtonColor: "#6366f1",
        timer: 2000,
        timerProgressBar: true,
        background: "#ffffff"
      });

      reset();
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        title: "Error!",
        text: "Failed to create package. Please try again.",
        icon: "error",
        confirmButtonColor: "#ef4444",
        background: "#ffffff"
      });
    }
  };

  const inputClasses = "w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";
  const errorClasses = "text-xs text-red-500 mt-1 flex items-center gap-1";

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg mb-4">
              <FaBox className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Create New Package
            </h1>
            <p className="text-gray-500 mt-2 text-sm">Design a beautiful package that your customers will love</p>
          </motion.div>
        </div>

        {/* Form Card */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          {/* Form Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FiPackage className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-xl">Package Information</h2>
                <p className="text-indigo-100 text-sm mt-0.5">Fill in the details to create a new package</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Package Title */}
            <div>
              <label className={labelClasses}>
                Package Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiPackage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  {...register('title')}
                  className={`${inputClasses} pl-10 ${errors.title ? 'border-red-500' : ''}`}
                  placeholder="Enter package title (e.g., Premium Membership)"
                />
              </div>
              {errors.title && (
                <p className={errorClasses}>
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Price */}
            <div>
              <label className={labelClasses}>
                Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                <input
                  type="number"
                  {...register('price')}
                  className={`${inputClasses} pl-8 ${errors.price ? 'border-red-500' : ''}`}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              {errors.price && (
                <p className={errorClasses}>
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.price.message}
                </p>
              )}
            </div>

            {/* Plot Dimensions Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FaRulerCombined className="w-4 h-4 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Plot Details</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Width */}
                <div>
                  <label className={labelClasses}>
                    Width (feet) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiGrid className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      {...register('width')}
                      className={`${inputClasses} pl-10 ${errors.width ? 'border-red-500' : ''}`}
                      placeholder="Enter width in feet"
                    />
                  </div>
                  {errors.width && (
                    <p className={errorClasses}>
                      <FiAlertCircle className="w-3 h-3" />
                      {errors.width.message}
                    </p>
                  )}
                </div>

                {/* Length */}
                <div>
                  <label className={labelClasses}>
                    Length (feet) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FiGrid className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      {...register('length')}
                      className={`${inputClasses} pl-10 ${errors.length ? 'border-red-500' : ''}`}
                      placeholder="Enter length in feet"
                    />
                  </div>
                  {errors.length && (
                    <p className={errorClasses}>
                      <FiAlertCircle className="w-3 h-3" />
                      {errors.length.message}
                    </p>
                  )}
                </div>

                {/* Plot Number */}
                <div>
                  <label className={labelClasses}>Plot Number</label>
                  <div className="relative">
                    <FiGrid className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      {...register('plotNumber')}
                      className={`${inputClasses} pl-10`}
                      placeholder="Enter plot number (e.g., A1)"
                    />
                  </div>
                </div>

                {/* Plot Status */}
                <div>
                  <label className={labelClasses}>Plot Status</label>
                  <Controller
                    name="plotStatus"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className={`${inputClasses} cursor-pointer`}>
                        <option value="available">Available</option>
                        <option value="sold">Sold</option>
                        <option value="booked">Booked</option>
                      </select>
                    )}
                  />
                </div>

                {/* Facing */}
                <div>
                  <label className={labelClasses}>Facing</label>
                  <Controller
                    name="facing"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className={`${inputClasses} cursor-pointer`}>
                        <option value="North">North</option>
                        <option value="South">South</option>
                        <option value="East">East</option>
                        <option value="West">West</option>
                        <option value="North-East">North-East</option>
                        <option value="North-West">North-West</option>
                        <option value="South-East">South-East</option>
                        <option value="South-West">South-West</option>
                      </select>
                    )}
                  />
                </div>

                {/* Location */}
                <div>
                  <label className={labelClasses}>Location</label>
                  <div className="relative">
                    <FiMapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      {...register('location')}
                      className={`${inputClasses} pl-10`}
                      placeholder="Enter plot location"
                    />
                  </div>
                </div>

                {/* Row */}
                <div>
                  <label className={labelClasses}>Grid Row (1-based)</label>
                  <input
                    type="number"
                    {...register('row')}
                    className={inputClasses}
                    placeholder="1"
                    min="1"
                  />
                </div>

                {/* Col */}
                <div>
                  <label className={labelClasses}>Grid Column (1-based)</label>
                  <input
                    type="number"
                    {...register('col')}
                    className={inputClasses}
                    placeholder="1"
                    min="1"
                  />
                </div>

                {/* Grid Width */}
                <div>
                  <label className={labelClasses}>Grid Width</label>
                  <input
                    type="number"
                    {...register('gridWidth')}
                    className={inputClasses}
                    placeholder="1"
                    min="1"
                  />
                </div>

                {/* Grid Height */}
                <div>
                  <label className={labelClasses}>Grid Height</label>
                  <input
                    type="number"
                    {...register('gridHeight')}
                    className={inputClasses}
                    placeholder="1"
                    min="1"
                  />
                </div>

                {/* Size Category */}
                <div>
                  <label className={labelClasses}>Size Category</label>
                  <Controller
                    name="sizeCategory"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className={`${inputClasses} cursor-pointer`}>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                        <option value="xl">XL</option>
                        <option value="corner">Corner</option>
                        <option value="premium">Premium</option>
                      </select>
                    )}
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="flex gap-8">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Controller
                    name="isCorner"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                    )}
                  />
                  <span className="text-gray-700 font-medium">Corner Plot</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <Controller
                    name="isPremium"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                    )}
                  />
                  <span className="text-gray-700 font-medium">Premium Plot</span>
                </label>
              </div>

              {/* Description */}
              <div>
                <label className={labelClasses}>Description</label>
                <textarea
                  {...register('description')}
                  rows={3}
                  className={inputClasses}
                  placeholder="Enter plot description"
                />
              </div>

              {/* Calculated Area */}
              {calculatedArea && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-2 p-3 bg-indigo-50 rounded-xl border border-indigo-100"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiInfo className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-medium text-indigo-800">Total Area</span>
                    </div>
                    <span className="text-lg font-bold text-indigo-600">
                      {calculatedArea.toLocaleString()} sq.ft.
                    </span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Status */}
            <div>
              <label className={labelClasses}>
                Package Status
              </label>
              <div className="relative">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className={`${inputClasses} pl-10 cursor-pointer`}
                    >
                      <option value="active">✓ Active - Available for purchase</option>
                      <option value="inactive">✗ Inactive - Hidden from users</option>
                    </select>
                  )}
                />
                <FaToggleOn className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Inactive packages will not be visible to customers
              </p>
            </div>

            {/* Preview Section */}
            {watchedWidth && watchedLength && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100"
              >
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiCheckCircle className="w-4 h-4 text-emerald-600" />
                  Package Preview
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Title:</span>
                    <span className="font-medium text-gray-800">{watch('title') || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Price:</span>
                    <span className="font-bold text-indigo-600">₹{watch('price')?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dimensions:</span>
                    <span className="font-medium text-gray-800">{watchedWidth} ft x {watchedLength} ft</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Area:</span>
                    <span className="font-semibold text-emerald-600">{calculatedArea?.toLocaleString()} sq.ft.</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${watch('status') === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                      {watch('status') === 'active' ? <FiCheckCircle className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
                      {watch('status') === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating Package...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Create Package
                    <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            {/* Security Note */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <FiShield className="w-3 h-3" />
                Package information is securely stored
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddNewPackage;